/**
 * useToast — app-wide transient toast notifications.
 *
 * Sibling to useConfirm (#1146): closes the "no toast system" gap that
 * left ~16 error/info paths reaching for native `window.alert()`
 * (blocking, unstyled, un-themed). Mount <ToastProvider> once at the
 * root, then any component does:
 *
 *   const toast = useToast()
 *   toast({ title: 'Schedule saved', variant: 'success' })
 *   toast({ title: 'Failed to save', description: msg, variant: 'error' })
 *
 * Unlike confirm (one modal that blocks the caller waiting for an
 * answer), toasts STACK and are fire-and-forget — the call returns
 * immediately. Each auto-dismisses (errors linger longer so they can
 * actually be read) and can be dismissed manually. The hook throws
 * outside a provider, matching useConfirm's loud-failure contract —
 * a missing provider is a wiring bug, not something to swallow. #1149.
 *
 * Sticky vs transient (#1214). The original implementation auto-
 * dismissed errors after 8s, which was still too short for the cases
 * the issue surfaced — operators missed actionable errors. Now:
 *   - success/info default to `transient` — auto-dismiss after 4s
 *   - error defaults to `sticky` — no auto-dismiss; operator dismisses
 *     it explicitly via the X button or an action
 * The legacy `durationMs: 0` ("0 means sticky") still works as the
 * explicit override. Pass `kind: 'transient' | 'sticky'` to override
 * the variant default — e.g. a success toast that needs to be sticky
 * because it carries a follow-up action.
 *
 * Actions (#1214). Error toasts often need a follow-up — "Retry" or
 * "Copy details" so the operator can paste the raw underlying message
 * into a support ticket without it being the toast's headline. Pass
 * `actions: [{ label, onClick }]`; they render as small links inside
 * the toast body. The toast auto-dismisses after `onClick` runs unless
 * the kind is explicitly `sticky` (in which case the operator probably
 * wants to see the result of their click before the toast disappears).
 *
 * Copy details (#1214). The companion to `actions`: pass `rawDetails`
 * along with the friendly `description`, and the toast gets a built-
 * in "Copy details" action that opens a small dialog with the raw
 * underlying message in a copyable textarea. The friendly fallback
 * stays on the toast; the raw text stays one click away.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type ToastVariant = 'error' | 'success' | 'info'
export type ToastKind = 'transient' | 'sticky'

export interface ToastAction {
  label: string
  /** Called when the action is clicked. Returning a Promise is fine —
   * the toast dismisses (unless sticky) without awaiting. */
  onClick: () => void | Promise<void>
}

export interface ToastOptions {
  title: string
  description?: ReactNode
  /** Visual treatment + a11y role. Defaults to 'info'. */
  variant?: ToastVariant
  /**
   * Lifecycle. `transient` auto-dismisses after the variant's default
   * duration; `sticky` stays until the operator dismisses it.
   * Defaults: error → sticky, success/info → transient.
   */
  kind?: ToastKind
  /** Override auto-dismiss in ms. 0 keeps it until dismissed manually.
   * Defaults: transient 4s. Sticky ignores this. */
  durationMs?: number
  /** Optional follow-up actions, rendered as inline links inside the
   * toast body. Typical pair on error toasts: "Retry" + "Copy details". */
  actions?: ToastAction[]
  /** Raw underlying error text the friendly `description` replaced —
   * when set, the toast renders a built-in "Copy details" action that
   * opens a small dialog with the text in a copyable textarea. */
  rawDetails?: string
}

interface ActiveToast extends ToastOptions {
  id: number
  variant: ToastVariant
  kind: ToastKind
}

type ToastFn = (opts: ToastOptions) => void

const ToastContext = createContext<ToastFn | null>(null)

// Transient default durations by variant. Sticky toasts ignore these.
const TRANSIENT_DURATION_MS: Record<ToastVariant, number> = {
  error: 4000,
  success: 4000,
  info: 4000,
}

/** Errors are sticky by default — the issue this primitive grew out of
 * (#1214) was operators missing error toasts that auto-dismissed before
 * they could act. Success/info stay transient. */
function defaultKindFor(variant: ToastVariant): ToastKind {
  return variant === 'error' ? 'sticky' : 'transient'
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([])
  // Monotonic counter for stable keys — avoids the Date.now() collision
  // two toasts fired in the same tick would hit.
  const idRef = useRef(0)
  // Track auto-dismiss timers so a manual dismiss clears the pending one
  // (otherwise the timer fires later and filters an already-gone id — a
  // harmless no-op, but leaks the timer until then).
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  // Clear every pending auto-dismiss timer when the provider unmounts —
  // otherwise a lingering setTimeout fires after teardown and setStates on
  // an unmounted tree (React warning + leak). Captured-ref pattern: read
  // timers.current inside the cleanup, not at effect-setup time. (Copilot #1161)
  useEffect(() => {
    const timersAtMount = timers.current
    return () => {
      for (const timer of timersAtMount.values()) clearTimeout(timer)
      timersAtMount.clear()
    }
  }, [])

  const toast = useCallback<ToastFn>(
    (opts) => {
      const id = idRef.current++
      const variant = opts.variant ?? 'info'
      const kind = opts.kind ?? defaultKindFor(variant)
      setToasts((prev) => [...prev, { ...opts, id, variant, kind }])
      // Sticky toasts never auto-dismiss. Transient: explicit `durationMs`
      // wins (0 still means "stay until dismissed", matching the legacy
      // override callers relied on before `kind` existed).
      if (kind === 'transient') {
        const duration = opts.durationMs ?? TRANSIENT_DURATION_MS[variant]
        if (duration > 0) {
          timers.current.set(
            id,
            setTimeout(() => dismiss(id), duration),
          )
        }
      }
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Bottom-right stack. pointer-events-none on the container so it
          never blocks clicks on the page behind it; each card re-enables
          pointer events for its own dismiss button. */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ActiveToast
  onDismiss: () => void
}) {
  // Local UI state for the "Copy details" dialog — opened via the built-
  // in action, scoped to this toast so multiple toasts can each have
  // their own copy dialog open independently.
  const [copyOpen, setCopyOpen] = useState(false)
  // Left accent bar carries the variant color; the card itself stays on
  // the themed card surface so it reads as part of the app in both
  // light and dark mode.
  const accent =
    toast.variant === 'error'
      ? 'border-l-destructive'
      : toast.variant === 'success'
        ? 'border-l-success'
        : 'border-l-primary'
  // Errors announce assertively (interrupt the screen reader); success/
  // info are polite status updates.
  const ariaRole = toast.variant === 'error' ? 'alert' : 'status'

  // Compose visible actions: caller-provided first, then the built-in
  // "Copy details" if rawDetails is set. The built-in action stays at
  // the end so it never shadows whatever Retry-like primary the caller
  // wired up.
  const actions: ToastAction[] = [
    ...(toast.actions ?? []),
    ...(toast.rawDetails
      ? [{ label: 'Copy details', onClick: () => setCopyOpen(true) }]
      : []),
  ]

  // When the caller's action runs, dismiss the toast for them — UNLESS
  // it's the built-in copy-details action (which opens a dialog the user
  // is about to read) or the toast is sticky (operator probably wants to
  // see the result of their click before the toast disappears).
  function runAction(action: ToastAction) {
    const isCopyDetails = action.label === 'Copy details' && !!toast.rawDetails
    void action.onClick()
    if (!isCopyDetails && toast.kind === 'transient') onDismiss()
  }

  return (
    <>
      <div
        role={ariaRole}
        className={`pointer-events-auto relative rounded-md border border-l-4 ${accent} bg-card p-3 pr-8 text-card-foreground shadow-lg animate-in slide-in-from-right-4 fade-in`}
      >
        <p className="text-sm font-medium">{toast.title}</p>
        {toast.description && (
          // whitespace-pre-line preserves the \n\n in multi-line messages
          // (e.g. the scan-failure hint) without an HTML rewrite.
          <p className="mt-0.5 whitespace-pre-line text-xs text-muted-foreground">
            {toast.description}
          </p>
        )}
        {actions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {actions.map((action, i) => (
              <button
                key={`${action.label}-${i}`}
                type="button"
                onClick={() => runAction(action)}
                className="text-primary underline-offset-2 hover:underline"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="absolute right-2 top-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          ✕
        </button>
      </div>
      {copyOpen && toast.rawDetails && (
        <CopyDetailsDialog
          details={toast.rawDetails}
          onClose={() => setCopyOpen(false)}
        />
      )}
    </>
  )
}

/**
 * Small modal-ish overlay for the "Copy details" pattern — shows the raw
 * underlying error text in a copyable textarea. Not a full Dialog primitive
 * because the toast is already a passive surface; this is a one-off
 * "select-all + copy" affordance, not a flow that needs focus-trapping or
 * routing. ESC closes it; clicking the backdrop closes it.
 */
function CopyDetailsDialog({
  details,
  onClose,
}: {
  details: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function doCopy() {
    try {
      await navigator.clipboard.writeText(details)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard write can fail (permissions, insecure context). The
      // textarea is selectable manually as a fallback; no toast-on-toast.
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Error details"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-md border bg-card p-4 text-card-foreground shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold">Error details</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Copy and paste this into a support ticket if you need help.
        </p>
        <textarea
          readOnly
          value={details}
          className="mt-2 h-32 w-full resize-none rounded border bg-background p-2 font-mono text-xs"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border px-3 py-1 text-xs hover:bg-muted"
          >
            Close
          </button>
          <button
            type="button"
            onClick={doCopy}
            className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:opacity-90"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function useToast(): ToastFn {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>')
  return ctx
}
